import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  contentChild,
  effect,
  forwardRef,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Combobox, ComboboxDialog, ComboboxInput, ComboboxPopupContainer } from '@angular/aria/combobox';
import { Listbox, Option } from '@angular/aria/listbox';
import { AriaFieldPrefixDirective } from '../directives/aria-field-prefix.directive';
import { AriaFieldSuffixDirective } from '../directives/aria-field-suffix.directive';
import { AriaFieldOption } from '../models/aria-field-option.model';
import type { AriaMobileDrawerMode } from '../models/aria-mobile-drawer.model';
import { AriaListboxScrollActiveDirective } from '../directives/aria-listbox-scroll-active.directive';
import { closeAriaCombobox } from '../utils/aria-combobox-close';
import { handleAriaReadonlyComboboxTriggerKeydown } from '../utils/aria-combobox-trigger-keydown';
import { createAriaFieldId } from '../utils/aria-field-id';
import { resolveAriaMobileDrawer } from '../utils/aria-mobile-drawer';
import { AppIconComponent } from '../app-icon/app-icon.component';
import { GenericService } from '../../../services/generic.service';

/**
 * Campo con ricerca testuale e suggerimenti filtrati (pattern WAI-ARIA Autocomplete).
 *
 * Su mobile (`mobileDrawer="auto"`) il trigger è readonly e la ricerca avviene nel bottom sheet.
 */
@Component({
  selector: 'app-aria-autocomplete-field',
  standalone: true,
  imports: [Combobox, ComboboxDialog, ComboboxInput, ComboboxPopupContainer, Listbox, Option, AriaListboxScrollActiveDirective, TitleCasePipe, AppIconComponent],
  templateUrl: './aria-autocomplete-field.component.html',
  styleUrl: './aria-autocomplete-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AriaAutocompleteFieldComponent),
      multi: true,
    },
  ],
})
export class AriaAutocompleteFieldComponent implements ControlValueAccessor {
  private readonly generic = inject(GenericService);
  private readonly comboboxRef = viewChild<Combobox<unknown>>('comboboxRef');
  private readonly drawerSearchRef = viewChild<ElementRef<HTMLInputElement>>('drawerSearchRef');
  protected readonly hasPrefix = contentChild(AriaFieldPrefixDirective);
  protected readonly hasSuffix = contentChild(AriaFieldSuffixDirective);

  readonly label = input<string>('');
  /** `floating`: label dentro il campo (stile legacy preventivatore). `stacked`: label sopra il controllo. */
  readonly labelLayout = input<'stacked' | 'floating'>('floating');
  readonly placeholder = input('Cerca...');
  readonly options = input<AriaFieldOption[]>([]);
  readonly required = input(false, { transform: (v: boolean | string) => v === true || v === '' });
  readonly hint = input<string | null>(null);
  readonly errorMessage = input<string | null>(null);
  readonly name = input<string | undefined>(undefined);
  readonly mobileDrawer = input<AriaMobileDrawerMode>('auto');

  protected readonly fieldId = createAriaFieldId('aria-autocomplete');
  protected readonly hintId = `${this.fieldId}-hint`;
  protected readonly errorId = `${this.fieldId}-error`;
  protected readonly drawerTitleId = `${this.fieldId}-drawer-title`;
  protected readonly searchValue = model('');
  protected readonly drawerSearchQuery = signal('');
  protected readonly selectedValues = model<string[]>([]);
  protected readonly isDisabled = signal(false);
  protected readonly isPopupExpanded = signal(false);
  private readonly wasExpanded = this.isPopupExpanded;
  private openedOnce = false;

  protected readonly useMobileDrawer = computed(() =>
    resolveAriaMobileDrawer(this.mobileDrawer(), this.generic.isMobile())
  );

  protected readonly triggerLabel = computed(() => {
    const value = this.selectedValues()[0];
    if (!value) {
      return '';
    }
    const label = this.options().find((option) => option.value === value)?.label ?? value;
    return this.formatDisplayLabel(label);
  });

  protected readonly filteredOptions = computed(() => {
    const query = (
      this.useMobileDrawer() ? this.drawerSearchQuery() : this.searchValue()
    )
      .trim()
      .toLowerCase();
    if (!query) {
      return this.options();
    }
    return this.options().filter((option) => option.label.toLowerCase().includes(query));
  });

  /**
   * Valori mostrati nel listbox del drawer.
   * Durante la ricerca non passiamo la selezione corrente: ngListbox la rimuoverebbe
   * quando l'opzione esce dal filtro e emetterebbe valuesChange([]) chiudendo il drawer.
   */
  protected readonly drawerListboxValues = computed(() => {
    if (this.drawerSearchQuery().trim()) {
      return [];
    }
    return this.selectedValues();
  });

  protected readonly useFloatingLabel = computed(
    () => this.labelLayout() === 'floating' && !!this.label(),
  );
  protected readonly isFilled = computed(() => {
    if (this.selectedValues()[0]) {
      return true;
    }
    const text = this.useMobileDrawer() ? this.triggerLabel() : this.searchValue();
    return text.trim().length > 0;
  });

  protected inputPlaceholder(): string {
    return this.useFloatingLabel() ? '' : this.placeholder();
  }

  private readonly titleCase = new TitleCasePipe();

  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private syncingFromForm = false;

  constructor() {
    effect(() => {
      const combobox = this.comboboxRef();
      const expanded = Boolean(combobox?.expanded());

      if (expanded && !this.wasExpanded()) {
        this.openedOnce = true;
      }

      const hasValue = Boolean(this.selectedValues()[0]);
      if (!expanded && this.wasExpanded() && this.openedOnce && !hasValue) {
        this.onTouched();
      }

      this.wasExpanded.set(expanded);
    });

    effect(() => {
      const combobox = this.comboboxRef();
      const expanded = Boolean(combobox?.expanded());
      this.isPopupExpanded.set(expanded);

      if (!expanded || !this.useMobileDrawer()) {
        return;
      }
      this.drawerSearchQuery.set('');
    });
  }

  protected describedBy(): string | null {
    const ids: string[] = [];
    if (this.hint()) {
      ids.push(this.hintId);
    }
    if (this.errorMessage()) {
      ids.push(this.errorId);
    }
    return ids.length ? ids.join(' ') : null;
  }

  protected onValuesChange(values: string[]): void {
    if (this.syncingFromForm) {
      return;
    }

    const value = values[0] ?? null;

    if (this.useMobileDrawer()) {
      if (value == null) {
        return;
      }
      const previous = this.selectedValues()[0] ?? null;
      if (value === previous) {
        return;
      }
    }

    this.selectedValues.set(values);
    const option = this.options().find((item) => item.value === value);
    if (option) {
      this.searchValue.set(this.formatDisplayLabel(option.label));
    }
    this.onChange(value);
    this.onTouched();
    closeAriaCombobox(this.comboboxRef());
  }

  protected onSearchChange(value: string): void {
    if (this.syncingFromForm) {
      return;
    }

    this.searchValue.set(value);
    if (!value.trim()) {
      this.selectedValues.set([]);
      this.onChange(null);
    }
  }

  protected onDrawerOptionSelect(option: AriaFieldOption, event: Event): void {
    if (option.disabled) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.onValuesChange([option.value]);
  }

  protected onDrawerSearchPointerDown(event: PointerEvent): void {
    event.stopPropagation();
  }

  protected onDrawerSearchInput(event: Event): void {
    event.stopPropagation();
    this.drawerSearchQuery.set((event.target as HTMLInputElement).value);
  }

  protected onDrawerSearchKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      closeAriaCombobox(this.comboboxRef());
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const input = document.getElementById(this.fieldId) as HTMLInputElement | null;
      if (!input) {
        return;
      }
      input.focus();
      input.dispatchEvent(
        new KeyboardEvent('keydown', { key: event.key, bubbles: true, cancelable: true })
      );
    }
  }

  protected onTriggerFocus(): void {
    if (this.isDisabled() || this.useMobileDrawer()) {
      return;
    }

    const combobox = this.comboboxRef();
    if (combobox && !combobox.expanded()) {
      combobox.open();
    }
  }

  protected formatDisplayLabel(label: string): string {
    return this.titleCase.transform(label) ?? label;
  }

  protected onTriggerKeydown(event: KeyboardEvent): void {
    if (this.isDisabled()) return;
    const combobox = this.comboboxRef();

    if (this.useMobileDrawer()) {
      handleAriaReadonlyComboboxTriggerKeydown(event, combobox, false);
      return;
    }

    if (combobox?.expanded()) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeAriaCombobox(combobox);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      combobox?.open();
    }
  }

  writeValue(value: string | null): void {
    this.syncingFromForm = true;
    if (value == null || value === '') {
      this.selectedValues.set([]);
      this.searchValue.set('');
    } else {
      this.selectedValues.set([value]);
      const option = this.options().find((item) => item.value === value);
      this.searchValue.set(option ? this.formatDisplayLabel(option.label) : value);
    }
    this.syncingFromForm = false;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }
}
